import React, { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import parse from 'html-react-parser';
import { getDecimal } from "../global/utils";
import { useStyles } from '../styles/styles';
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from '@mui/material/useMediaQuery'
import Typography from "@mui/material/Typography";
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import FormControl from "@mui/material/FormControl";
import FormLabel from '@mui/material/FormLabel';
import Button from "@mui/material/Button";
import { NumericFormat } from "react-number-format";
import ReplayIcon from "@mui/icons-material/Replay";
import AddShoppingCartIcon from '@mui/icons-material/AddShoppingCart';
import Select from "@mui/material/Select";

const AddFee = (props) => {
    const payableFee = props.payableFee;
    const addSelected = props.addSelected;
    const allFees = props.allFees;
    const feeClicked = props.feeClicked;
    const { register, handleSubmit, setValue } = useForm();
    const classes = useStyles;
    const [itemAmount, setItemAmount] = useState(0);
    const [quantity, setQuantity] = useState(0);
    const [totalAmt, setTotalAmt] = useState(0);
    const [selectedFee, setSelectedFee] = useState('');
    const theme = useTheme();
    const smallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const quantityChanged = (e) => {
        let qty = 0;
        const qtyVal = e.target.value;
        if (qtyVal) qty = parseInt(qtyVal);
        setQuantity(qty);
        if (qty > 0 && itemAmount > 0) {
            setTotalAmt(qty * itemAmount);
        } else {
            setTotalAmt(0);
        }
    }

    const itemAmtChanged = (values) => {
        if (values) {
            const amt = values.floatValue;
            setItemAmount(amt);
            if (quantity > 0) {
                setTotalAmt(amt * quantity);
            } else {
                setTotalAmt(0);
            }
        } else {
            setTotalAmt(getDecimal(0))
        }
    }

    const addSelectedFee = (form) => {
        if (!payableFee) return;
        const newMiscFee = {
            key: Date.now(),
            cmsId: payableFee.cmsId,
            name: payableFee.name,
            amount: totalAmt,
            quantity: quantity,
            itemAmount: itemAmount,
            comments: form.comments,
            deliveryMethod: false
        }
        addSelected(newMiscFee);
        clearFee();
    }

    const clearFee = () => {
        setValue('feeSelect', '');
        setValue('enteredAmount', '');
        setValue('quantity', '');
        setValue('totalAmount', '');
        setValue('comments', '');
        setItemAmount(0);
        setQuantity(0);
        setTotalAmt(0);
        setSelectedFee('');
    }

    const handleFeeChange = (e) => {
        const feeName = e.target.value;
        setSelectedFee(feeName);
        feeClicked(e);
    }

    useEffect(() => {
        if (payableFee) {
            const amt = payableFee.allowAmountEntry ? 0 : payableFee.amount;
            setItemAmount(amt);
            setQuantity(1);
            setTotalAmt(amt);
        }
    }, [payableFee])

    return (
        <>
            <Typography variant={smallScreen ? 'subtitle1' : 'h6'} component='h2' sx={{...classes.title, marginTop: {xs: '0', md: '.5rem'}}}>Add a Fee</Typography>
            <Paper elevation={3} sx={{...classes.tableContainer, padding: '1rem'}}>
                <Grid container spacing={1} style={{width: '100%', fontSize: '.9rem'}}>
                    {allFees && (
                        <Grid item xs={6} md={3}>
                            <FormControl component='fieldset' fullWidth sx={classes.formControl} {...register('feeSelect')}>
                                <FormLabel component="legend" htmlFor='feeSelect' sx={classes.inputLabel}>
                                    Fee Name
                                </FormLabel>
                                <Select
                                    id='feeSelect'
                                    name='feeSelect'
                                    native
                                    variant='outlined'
                                    fullWidth
                                    sx={classes.numberbox}
                                    onChange={handleFeeChange}
                                    inputProps={{id: 'feeSelect', 'aria-label': 'Fee Name'}}
                                >
                                    <option key='-1' value=''>None</option>
                                    {allFees.map((fee) => (
                                        <option key={fee.name} value={fee.name} style={{fontSize: '.9rem'}}>{fee.name}</option>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                    )}
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth {...register('enteredAmount')}>
                            <FormLabel htmlFor='amount' shrink='true' sx={classes.inputLabel}>
                                {payableFee && payableFee.allowAmountEntry ? 'Enter' : 'Fee'} Amount
                            </FormLabel>
                            <NumericFormat
                                id='amount'
                                name='enteredAmount'
                                customInput={TextField}
                                InputProps={{readOnly: payableFee ? !payableFee.allowAmountEntry : true, sx: {...classes.numberbox}, disabled: selectedFee === ''}}
                                type='text'
                                autoComplete='off'
                                variant='outlined'
                                allowEmptyFormatting mask="_"
                                decimalScale={2}
                                decimalSeparator='.'
                                fixedDecimalScale={true}
                                value={itemAmount}
                                onValueChange={values => {itemAmtChanged(values)}}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth>
                            <FormLabel htmlFor='quantity' shrink='true' sx={classes.inputLabel}>
                                Quantity
                            </FormLabel>
                            <TextField
                                id='quantity'
                                name='name'
                                variant='outlined'
                                type='number'
                                value={quantity}
                                inputProps={{sx: classes.textbox}}
                                {...register('quantity')}
                                onChange={quantityChanged}
                                disabled={selectedFee === ''}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <FormControl fullWidth>
                            <FormLabel htmlFor='total' shrink='true' sx={classes.inputLabel}>
                                Total Amount
                            </FormLabel>
                            <Typography
                                id='total'
                                name='totalAmount'
                                sx={{
                                    padding: '0 1rem',
                                    height: '1.9rem',
                                    display: 'flex',
                                    alignItems: 'center'
                                }}
                            >
                                {getDecimal(totalAmt, false)}
                            </Typography>
                        </FormControl>
                    </Grid>
                    {payableFee && payableFee.payorInstructions && (
                        <>
                            <Grid item xs={4} md={3} sx={{marginTop: '.8rem'}}>
                                <Typography variant='body1' component='h5' sx={classes.title}>Entry Instructions:</Typography>
                            </Grid>
                            <Grid item xs={8} md={9} sx={{marginTop: '.8rem'}}>
                                <Typography variant='subtitle2' color='primary' sx={{paddingTop: '2pt'}}>{parse(payableFee.payorInstructions)}</Typography>
                            </Grid>
                        </>
                    )}
                    <Grid xs={12} >
                        <FormControl fullWidth sx={{...classes.formControl, margin: '.5rem'}}>
                            <FormLabel htmlFor='request details' shrink='true' sx={classes.inputLabel}>
                                Request Details
                            </FormLabel>
                            <TextField
                                id='request details'
                                aria-label='Request details'
                                fullWidth
                                multiline
                                rows={3}
                                sx={{resize: 'none'}}
                                {...register('comments')}
                                inputProps={{id: 'request details', maxLength: 500, sx: {fontSize: '.9rem', lineHeight: '1rem'}}}
                                disabled={selectedFee === ''}
                            />
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} >
                        <Box component='span' sx={{display: 'flex', flexDirection: 'row-reverse'}}>
                            <Button
                                variant='contained'
                                color='primary'
                                startIcon={<AddShoppingCartIcon />}
                                sx={{...classes.squashedButton, fontSize: smallScreen ? '.6rem' : 'inherit'}}
                                onClick={handleSubmit(addSelectedFee)}
                                disabled={selectedFee === ''}
                            >
                                Add Fee
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                startIcon={<ReplayIcon/>}
                                sx={classes.squashedButton}
                                onClick={clearFee}
                            >
                                Clear
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>
        </>
    );
}

export default AddFee;